/**
 * Le dessin d'un vocal : des barres. Deux sources, le MÊME nombre de barres pour que la
 * vignette ne saute pas quand le lecteur prend la place :
 *
 *  - `barresDecoratives(graine)` — déterministes, sans rien décoder : mêmes barres pour une
 *    même graine (stables au re-rendu), distinctes d'un vocal à l'autre. Pour les grilles
 *    denses (50 vocaux, zéro téléchargement) et les vignettes.
 *  - `decoderLOnde(src, charger)` — la vraie onde, décodée par WebAudio (RMS par tranche) à
 *    partir des octets que l'APP donne (`charger(src)` : son `fetch`, ses cookies, sa signature —
 *    la librairie ne télécharge rien, vague 2, 02-11-2026). Le fichier entier y passe : réservée
 *    aux vues à UN vocal (une bulle, la visionneuse). Cache par adresse, trois décodages au plus
 *    en même temps ; `null` si le chargeur rend `null` ou si le décodage échoue (format) —
 *    l'appelant garde alors les barres décoratives.
 *
 * Reprise d'`apps/cockpit/src/shared/ui/audioBars.ts` et d'`AudioPlayer.tsx` (22-08-2026).
 */

/** Ce que l'app donne pour décoder une onde : les octets du vocal, ou `null` si elle ne les a pas. */
export type ChargeurDOnde = (src: string) => Promise<ArrayBuffer | null>;

/** Le nombre de barres d'une onde. */
export const NB_BARRES = 56;

export function barresDecoratives(graine: string, n: number = NB_BARRES): number[] {
  let etat = 2166136261;
  for (let i = 0; i < graine.length; i++) {
    etat ^= graine.charCodeAt(i);
    etat = Math.imul(etat, 16777619);
  }
  etat = etat >>> 0 || 1;
  const alea = () => {
    etat = (Math.imul(etat, 1664525) + 1013904223) >>> 0;
    return etat / 0xffffffff;
  };
  const phase = (etat % 7) - 3;
  const barres: number[] = [];
  for (let i = 0; i < n; i++) {
    const enveloppe = 0.4 + 0.6 * Math.abs(Math.sin((i / n) * Math.PI * 3 + phase));
    barres.push(Math.max(0.14, Math.min(1, enveloppe * (0.5 + alea() * 0.6))));
  }
  return barres;
}

/** Ramène des pics quelconques à `n` barres entre 0,14 et 1 (une onde fournie par l'app). */
export function normaliserLesBarres(pics: readonly number[], n: number = NB_BARRES): number[] {
  if (pics.length === 0) return [];
  const sortie: number[] = [];
  for (let i = 0; i < n; i++) {
    const debut = Math.floor((i / n) * pics.length);
    const fin = Math.max(debut + 1, Math.floor(((i + 1) / n) * pics.length));
    let max = 0;
    for (let j = debut; j < fin && j < pics.length; j++) max = Math.max(max, Math.abs(pics[j] ?? 0));
    sortie.push(max);
  }
  const plafond = Math.max(...sortie, 0.0001);
  return sortie.map((v) => Math.max(0.14, Math.min(1, v / plafond)));
}

const cache = new Map<string, number[]>();
const enVol = new Map<string, Promise<number[] | null>>();
let contexte: AudioContext | null = null;
let actifs = 0;
const MAX_ACTIFS = 3;
const file: Array<() => void> = [];

function leContexte(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctx =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  if (!contexte) contexte = new Ctx();
  return contexte;
}

function picsDuTampon(tampon: AudioBuffer, n: number): number[] {
  const canal = tampon.getChannelData(0);
  const bloc = Math.floor(canal.length / n) || 1;
  const pics: number[] = [];
  for (let i = 0; i < n; i++) {
    let somme = 0;
    for (let j = 0; j < bloc; j++) {
      const v = canal[i * bloc + j] ?? 0;
      somme += v * v;
    }
    pics.push(Math.sqrt(somme / bloc));
  }
  return normaliserLesBarres(pics, n);
}

/** La vraie onde d'un vocal, `null` si elle ne se décode pas. Les octets viennent de `charger`. */
export async function decoderLOnde(src: string, charger: ChargeurDOnde, n: number = NB_BARRES): Promise<number[] | null> {
  const deja = cache.get(src);
  if (deja) return deja;
  const vol = enVol.get(src);
  if (vol) return vol;
  const travail = (async (): Promise<number[] | null> => {
    await new Promise<void>((pret) => {
      if (actifs < MAX_ACTIFS) {
        actifs += 1;
        pret();
      } else {
        file.push(() => {
          actifs += 1;
          pret();
        });
      }
    });
    try {
      const ctx = leContexte();
      if (!ctx) return null;
      const octets = await charger(src);
      if (!octets) return null;
      // slice(0) : decodeAudioData détache le tampon qu'on lui donne.
      const tampon = await ctx.decodeAudioData(octets.slice(0));
      const barres = picsDuTampon(tampon, n);
      cache.set(src, barres);
      return barres;
    } catch {
      return null;
    } finally {
      actifs = Math.max(0, actifs - 1);
      file.shift()?.();
    }
  })();
  enVol.set(src, travail);
  try {
    return await travail;
  } finally {
    enVol.delete(src);
  }
}
